import { captureException } from '@sentry/react';
import { when } from 'mobx';
import type { UserInit } from '@gamiphy/loyalty-station-desktop/store/user/types';
import type { Hit, OrderData } from '@gamiphy/bl/analytics';
import type { Product } from '@gamiphy/bl/product';
import type { CartSdkStore } from '@gamiphy/loyalty-station-desktop/store/sdk';
import { delay } from '@gamiphy/utils/general';
import type { PixelMessage } from '@gamiphy/loyalty-station-desktop/store/sdk/cart/platforms/vtex';
import { CartData } from '@coretava/api/events-service';
import { WidgetsSdk } from './widgets';
import { StoreSdk } from './store';
import { SdkConfig } from './types';
import '../global';

export class LoyaltyStationSdk {
  public readonly store = new StoreSdk();

  public readonly widgets = new WidgetsSdk();

  public cart?: CartSdkStore;

  private initialized = false;

  public async init(config: SdkConfig): Promise<void> {
    try {
      if (!this.initialized && config.app) {
        this.addVtexListener();

        const [
          { CoreStore },
          { SdkStore },
        ] = await Promise.all([
          import('@gamiphy/loyalty-station-desktop/store/core'),
          import('@gamiphy/loyalty-station-desktop/store/sdk'),
        ]);

        await Promise.all([
          CoreStore.init(config.app, config),
          SdkStore.init({
            cart: config.cart,
            goToAuth: config.goToAuth,
          }),
        ]);

        this.cart = SdkStore.cart;

        this.initialized = true;
      }
    } catch (e) {
      console.error(e);
      captureException(e);
    }
  }

  public async getAppId(): Promise<string | undefined> {
    const { AppStore } = await import('@gamiphy/loyalty-station-desktop/store/app');

    return AppStore.appId;
  }

  public async login(user: UserInit): Promise<void> {
    try {
      const [
        { CoreStore },
        { UserStore },
      ] = await Promise.all([
        import('@gamiphy/loyalty-station-desktop/store/core'),
        import('@gamiphy/loyalty-station-desktop/store/user'),
      ]);

      await when(() => CoreStore.initialized);
      await UserStore.login(user);
    } catch (e) {
      captureException(e);
    }
  }

  public async logout(): Promise<void> {
    try {
      const [
        { CoreStore },
        { UserStore },
      ] = await Promise.all([
        import('@gamiphy/loyalty-station-desktop/store/core'),
        import('@gamiphy/loyalty-station-desktop/store/user'),
      ]);

      await when(() => CoreStore.initialized);
      UserStore.logout();
    } catch (e) {
      captureException(e);
    }
  }

  public async open(): Promise<void> {
    try {
      const [
        { CoreStore },
        { UiStore },
      ] = await Promise.all([
        import('@gamiphy/loyalty-station-desktop/store/core'),
        import('@gamiphy/loyalty-station-desktop/store/ui'),
      ]);

      await when(() => CoreStore.initialized);
      UiStore.open();
    } catch (e) {
      captureException(e);
    }
  }

  public async close(): Promise<void> {
    try {
      const [
        { CoreStore },
        { UiStore },
      ] = await Promise.all([
        import('@gamiphy/loyalty-station-desktop/store/core'),
        import('@gamiphy/loyalty-station-desktop/store/ui'),
      ]);

      await when(() => CoreStore.initialized);
      UiStore.close();
    } catch (e) {
      captureException(e);
    }
  }

  public async show(): Promise<void> {
    try {
      const [
        { CoreStore },
        { UiStore },
      ] = await Promise.all([
        import('@gamiphy/loyalty-station-desktop/store/core'),
        import('@gamiphy/loyalty-station-desktop/store/ui'),
      ]);

      await when(() => CoreStore.initialized);
      UiStore.show();
    } catch (e) {
      captureException(e);
    }
  }

  public async hide(): Promise<void> {
    try {
      const [
        { CoreStore },
        { UiStore },
      ] = await Promise.all([
        import('@gamiphy/loyalty-station-desktop/store/core'),
        import('@gamiphy/loyalty-station-desktop/store/ui'),
      ]);

      await when(() => CoreStore.initialized);
      UiStore.hide();
    } catch (e) {
      captureException(e);
    }
  }

  public async showProductNotification(productId: string): Promise<void> {
    try {
      const [
        { CoreStore },
        { UserStore },
        { ProductStore },
      ] = await Promise.all([
        import('@gamiphy/loyalty-station-desktop/store/core'),
        import('@gamiphy/loyalty-station-desktop/store/user'),
        import('@gamiphy/loyalty-station-desktop/store/products'),
      ]);

      await when(() => CoreStore.initialized && UserStore.isLoggedIn);
      await ProductStore.trackViewProduct(productId);
    } catch (e) {
      captureException(e);
    }
  }

  public async collectProductView(externalProductId: string): Promise<void> {
    try {
      const [
        { CoreStore },
        { ProductStore },
      ] = await Promise.all([
        import('@gamiphy/loyalty-station-desktop/store/core'),
        import('@gamiphy/loyalty-station-desktop/store/products'),
      ]);

      await when(() => CoreStore.initialized);
      await ProductStore.trackViewProduct(externalProductId);
    } catch (e) {
      captureException(e);
    }
  }

  public async collectAddToCart(externalProductId: string): Promise<void> {
    try {
      const [
        { AppStore },
        { AnalyticsFactory },
      ] = await Promise.all([
        import('@gamiphy/loyalty-station-desktop/store/app'),
        import('@gamiphy/analytics'),
      ]);

      await when(() => !!AppStore.appId);
      // Delay the popup to make sure that the product is added
      setTimeout(async () => {
        await this.cart?.onAddToCard(externalProductId);
      }, 1000);
      await AnalyticsFactory.collectAddToCart(externalProductId);
    } catch (e) {
      captureException(e);
    }
  }

  public async collectCheckoutStarted(cart: CartData): Promise<void> {
    try {
      const [
        { AppStore },
        { EventsStore },
      ] = await Promise.all([
        import('@gamiphy/loyalty-station-desktop/store/app'),
        import('@gamiphy/loyalty-station-desktop/store/events'),
        import('@gamiphy/analytics'),
      ]);

      await when(() => !!AppStore.appId);
      await EventsStore.createEvent({
        type: 'checkoutStarted',
        cart,
      });
    } catch (e) {
      captureException(e);
    }
  }

  public async collectOrderPlace(externalOrderId: string, data?: OrderData): Promise<void> {
    try {
      const [
        { AppStore },
        { AnalyticsFactory },
      ] = await Promise.all([
        import('@gamiphy/loyalty-station-desktop/store/app'),
        import('@gamiphy/analytics'),
      ]);

      await when(() => !!AppStore.appId);
      await AnalyticsFactory.collectOrderPlace(externalOrderId, data);
    } catch (e) {
      captureException(e);
    }
  }

  public async collect(hit: Omit<Hit, 'site'>): Promise<void> {
    try {
      const [
        { AppStore },
        { AnalyticsFactory },
      ] = await Promise.all([
        import('@gamiphy/loyalty-station-desktop/store/app'),
        import('@gamiphy/analytics'),
      ]);

      await when(() => !!AppStore.appId);
      await AnalyticsFactory.collect(hit);
    } catch (e) {
      captureException(e);
    }
  }

  public async getCustomerId(): Promise<string> {
    try {
      const [
        { AppStore },
        { AnalyticsFactory },
      ] = await Promise.all([
        import('@gamiphy/loyalty-station-desktop/store/app'),
        import('@gamiphy/analytics'),
      ]);

      const [analyticsInstance] = await Promise.all([
        AnalyticsFactory.getInstance(),
        when(() => !!AppStore.appId),
      ]);

      return analyticsInstance.customerId;
    } catch (e) {
      captureException(e);
    }
    return '';
  }

  public async getRecommendedProducts(limit?: number): Promise<Product[]> {
    try {
      const [
        { AppStore },
        { AnalyticsFactory },
        { RecommendationApi },
      ] = await Promise.all([
        import('@gamiphy/loyalty-station-desktop/store/app'),
        import('@gamiphy/analytics'),
        import('@gamiphy/gamix-modules/recommendation'),
      ]);

      const [analyticsInstance] = await Promise.all([
        AnalyticsFactory.getInstance(),
        when(() => !!AppStore.appId),
      ]);

      return await RecommendationApi.getRecommendedProducts(AppStore.appId!, analyticsInstance.customerId, limit);
    } catch (e) {
      captureException(e);
    }
    return [];
  }

  /**
   * fixme: Custom code for vtex because we currently cannot push new versions on it
   * @private
   */
  private addVtexListener(): void {
    window.addEventListener('message', async (e: PixelMessage) => {
      const { CoreStore } = await import('@gamiphy/loyalty-station-desktop/store/core');
      await when(() => CoreStore.initialized);

      switch (e.data.eventName) {
        case 'vtex:removeFromCart':
          await delay(1000);
          await this.cart?.onRemoveFromCart();
          break;
        case 'vtex:orderPlaced': {
          const { EventsStore } = await import('@gamiphy/loyalty-station-desktop/store/events');
          void this.cart?.onCartComplete();

          void EventsStore.createEvent({
            type: 'orderPlaced',
            order: {
              externalId: e.data.orderGroup,
              total: e.data.transactionTotal,
              date: e.data.transactionDate,
            },
          });
          break;
        }
        default:
        // do-nothing
      }
    });
  }
}
